import { TabPanel } from "@mui/lab";
import { useMemo } from "react";
import DataLoadingFailed from "@entrilia/shared/components/DataLoadingFailed";
import useFetch from "@entrilia/shared/hooks/useFetch";
import usePageTitle from "@entrilia/shared/hooks/usePageTitle";
import useTabSearchParams from "@entrilia/shared/hooks/useTabSearchParams";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import useClientContext from "../../../../context/useClientContext";
import storage from "../../../../storage/storage";
import GeneralPageHeader from "../../../common/GeneralPageHeader";
import PageTabs from "../../../common/PageTabs";
import TabLabelWithCount from "../../../common/TabLabelWithCount";
import AllDataSubmissionsList from "./AllDataSubmissionsList";
import MyDataSubmissionsList from "./MyDataSubmissionsList";

const tabValues = ["all", "my"] as const;

const tabPanelSx = { flex: 1, px: 3, pt: 2.5, width: "100%" };

const DataSubmissionsPage = () => {
  usePageTitle("Data Submissions");

  const { clientCode, hasAnyPermission } = useClientContext();

  const [assignedDataSubmissions, fetchError, { isFetching, fetch: fetchAssignedDataSubmissions }] = useFetch(
    adminApi.getAssignedDataSubmissions
  );

  const hasAccessToPortfolioMonitoringManagement = useMemo(
    () => hasAnyPermission(["ViewPortfolioMonitoring", "ManagePortfolioMonitoring"]),
    [hasAnyPermission]
  );

  const { selectedTab, onTabChange } = useTabSearchParams(
    tabValues,
    hasAccessToPortfolioMonitoringManagement ? "all" : "my"
  );

  if (fetchError) {
    logError(fetchError, "[DataSubmissionsPage] getAssignedDataSubmissions");
    storage.clearFilterState(`${clientCode}_my_data_submissions_filter_v1`);
    return <DataLoadingFailed title="Could not load data submissions" />;
  }

  return (
    <>
      <GeneralPageHeader title="Data Submissions" hideBorderLine />

      <PageTabs
        tabs={hasAccessToPortfolioMonitoringManagement ? tabValues : ["my"]}
        labels={
          hasAccessToPortfolioMonitoringManagement
            ? [
                "All Submissions",
                <TabLabelWithCount
                  key="my"
                  label="My Submissions"
                  count={(assignedDataSubmissions ?? []).filter((ds) => ds.status === "Pending").length}
                  isActive={selectedTab === "my"}
                  hideCounter={!assignedDataSubmissions?.length}
                />,
              ]
            : ["My Submissions"]
        }
        value={selectedTab}
        onChange={onTabChange}
      >
        {hasAccessToPortfolioMonitoringManagement && (
          <TabPanel value="all" sx={tabPanelSx}>
            <AllDataSubmissionsList />
          </TabPanel>
        )}
        <TabPanel value="my" sx={tabPanelSx}>
          <MyDataSubmissionsList
            assignedDataSubmissions={assignedDataSubmissions ?? []}
            loading={isFetching}
            onRefresh={fetchAssignedDataSubmissions}
          />
        </TabPanel>
      </PageTabs>
    </>
  );
};

export default DataSubmissionsPage;
